import { Component, inject, Injector, OnInit } from '@angular/core';
import { Database, get, ref, remove, set } from "@angular/fire/database";
import { AuthService } from 'src/app/services/auth.service';
import { BaseComponent } from 'src/app/shared/baseComponent';
import { FirebaseSchema } from 'src/app/virtex/schema/firebaseSchema';


@Component({
  selector: 'app-fbqueries',
  templateUrl: './fbqueries.component.html',
  styleUrls: ['./fbqueries.component.css']
})
export class FbqueriesComponent extends BaseComponent implements OnInit {
  database = inject(Database);
  isAuthenticatedWithFirebase = false
  showinfo = false
  loading = false
  nodeSizes: { node: string, sizeBytes: number, numChildren: number }[] = []
  tableSortCol: string = 'node'
  tableSortDir: 'asc' | 'desc' = 'asc'

  get sortedNodeSizes() {
    return [...this.nodeSizes].sort((a: any, b: any) => {
      let valA = a[this.tableSortCol]
      let valB = b[this.tableSortCol]
      if (typeof valA === 'string') valA = valA.toLowerCase()
      if (typeof valB === 'string') valB = valB.toLowerCase()
      if (valA < valB) return this.tableSortDir === 'asc' ? -1 : 1
      if (valA > valB) return this.tableSortDir === 'asc' ? 1 : -1
      return 0
    })
  }

  sortBy(column: string) {
    if (this.tableSortCol === column) {
      this.tableSortDir = this.tableSortDir === 'asc' ? 'desc' : 'asc'
    } else {
      this.tableSortCol = column
      this.tableSortDir = 'asc'
    }
  }
  // schema preview
  modalHandlerSchemaText = FirebaseSchema.pretty(FirebaseSchema.modalHandlerDefaults);
  showSchemaModal = false;
  showConfirmModal = false;
  pendingDeleteNode = '';

  constructor(injector: Injector, private authService: AuthService) {
    super(injector)
  }

  async ngOnInit(): Promise<void> {
    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfullLogin())
  }

  async signInWithGoogle() {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle()
    if (this.isAuthenticatedWithFirebase) { await this.onSuccessfullLogin() }
  }

  private async onSuccessfullLogin() {
    await this.getData()
  }

  // --- schema modal helpers ---
  openSchemaModal() { this.showSchemaModal = true; }
  closeSchemaModal() { this.showSchemaModal = false; }

  deleteNode(node: string) {
    this.pendingDeleteNode = node;
    this.showConfirmModal = true;
  }

  cancelDelete() {
    this.showConfirmModal = false;
    this.pendingDeleteNode = '';
  }

  async confirmDelete() {
    this.showConfirmModal = false;
    const node = this.pendingDeleteNode;
    this.pendingDeleteNode = '';
    this.loading = true
    this.loader.show()
    try {
      await remove(ref(this.database, node))
      this.toast.info(`All ${node} deleted successfully`)
      await this.getData()
    } catch (error) {
      console.error(`Error deleting ${node}:`, error)
      this.toast.danger(`Failed to delete ${node}`)
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }
  async copySchemaToClipboard() {
    try {
      await navigator.clipboard.writeText(this.modalHandlerSchemaText);
      this.toast.info('Schema copied to clipboard');
    } catch {
      this.toast.danger('Failed to copy schema');
    }
  }
  async getData() {
    this.loading = true
    this.loader.show()
    try {
      await this.scanSelectedNodes()
    } catch (error) {
      console.error('Error refreshing data:', error);
      this.toast.danger('Failed to refresh data')
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }

  async removeRenderlogs() {
    this.loading = true
    this.loader.show()
    try {
      const renderlogRef = ref(this.database, 'renderlogs');
      await remove(renderlogRef)

      // Refresh nodeSizes after removal
      await this.scanSelectedNodes()
      this.toast.success('All renderlog entries removed successfully')
    } catch (error) {
      console.error('Error removing renderlogs:', error);
      this.toast.danger('Failed to remove renderlog entries')
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }

  async removeEvents() {
    this.loading = true
    this.loader.show()
    try {
      const eventsRef = ref(this.database, 'events');
      await remove(eventsRef)

      // Refresh nodeSizes after removal
      await this.scanSelectedNodes()
      this.toast.success('All event entries removed successfully')
    } catch (error) {
      console.error('Error removing events:', error);
      this.toast.danger('Failed to remove event entries')
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }

  async reduceAll() {
    this.loading = true
    this.loader.show()
    try {
      // Remove renderlogs, events, and performance logs
      const renderlogRef = ref(this.database, 'renderlogs');
      const eventsRef = ref(this.database, 'events');
      const performanceRef = ref(this.database, 'performance');

      await Promise.all([
        remove(renderlogRef),
        remove(eventsRef),
        remove(performanceRef)
      ])

      // Remove old exercises (older than 2 days)
      const exercisesRef = ref(this.database, 'exercises');
      const exercisesSnap = await get(exercisesRef)

      let removedExercisesCount = 0
      if (exercisesSnap.exists()) {
        const exercises = exercisesSnap.val()
        const twoDaysAgo = new Date()
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)

        const removeExercisePromises: Promise<void>[] = []

        for (const [exerciseId, exercise] of Object.entries(exercises)) {
          const exerciseData = exercise as any
          if (exerciseData.startdate) {
            try {
              const startDate = new Date(exerciseData.startdate)
              if (!isNaN(startDate.getTime()) && startDate < twoDaysAgo) {
                const exerciseRef = ref(this.database, `exercises/${exerciseId}`)
                removeExercisePromises.push(remove(exerciseRef))
                removedExercisesCount++
              }
            } catch (dateError) {
              console.warn(`Invalid date format for exercise ${exerciseId}: ${exerciseData.startdate}`)
            }
          }
        }

        if (removeExercisePromises.length > 0) {
          await Promise.all(removeExercisePromises)
        }
      }

      // Refresh nodeSizes after all removals
      await this.scanSelectedNodes()
      this.toast.success(`All logs removed successfully. Removed ${removedExercisesCount} old exercise(s).`)
    } catch (error) {
      console.error('Error removing entries:', error);
      this.toast.danger('Failed to remove entries')
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }

  async removeOldExercises() {
    this.loading = true
    this.loader.show()
    try {
      const exercisesRef = ref(this.database, 'exercises');
      const exercisesSnap = await get(exercisesRef)

      if (!exercisesSnap.exists()) {
        this.toast.info('No exercises found to process')
        return
      }

      const exercises = exercisesSnap.val()
      const twoDaysAgo = new Date()
      twoDaysAgo.setDate(twoDaysAgo.getDate() - 2)

      let removedCount = 0
      const removePromises: Promise<void>[] = []

      for (const [exerciseId, exercise] of Object.entries(exercises)) {
        const exerciseData = exercise as any
        if (exerciseData.startdate) { // Note: lowercase 'startdate' as per JSON signature
          try {
            // Parse the date string in format "M/d/yyyy" (e.g., "9/5/2025")
            const startDate = new Date(exerciseData.startdate)

            // Check if the date is valid and older than 2 days
            if (!isNaN(startDate.getTime()) && startDate < twoDaysAgo) {
              const exerciseRef = ref(this.database, `exercises/${exerciseId}`)
              removePromises.push(remove(exerciseRef))
              removedCount++
            }
          } catch (dateError) {
            console.warn(`Invalid date format for exercise ${exerciseId}: ${exerciseData.startdate}`)
          }
        }
      }

      if (removePromises.length > 0) {
        await Promise.all(removePromises)
        await this.scanSelectedNodes()
        this.toast.success(`Successfully removed ${removedCount} old exercise(s)`)
      } else {
        this.toast.info('No exercises older than 2 days found')
      }
    } catch (error) {
      console.error('Error removing old exercises:', error);
      this.toast.danger('Failed to remove old exercises')
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }

  async removePerformanceLogs() {
    this.loading = true
    this.loader.show()
    try {
      const performanceRef = ref(this.database, 'performance');
      await remove(performanceRef)

      // Refresh nodeSizes after removal
      await this.scanSelectedNodes()
      this.toast.success('All performance log entries removed successfully')
    } catch (error) {
      console.error('Error removing performance logs:', error);
      this.toast.danger('Failed to remove performance log entries')
    } finally {
      this.loading = false
      this.loader.hide()
    }
  }
  async scanSelectedNodes() {
    const nodesToCheck = [
      'exercises', 'scenarios', 'performance', 'lessonplans',
      'measurements', 'renderlogs', 'test', 'surveys',
      'worlds', 'avatars', 'events'
    ]

    this.nodeSizes = []

    for (const key of nodesToCheck) {
      try {
        const nodeRef = ref(this.database, `/${key}`)
        const nodeSnap = await get(nodeRef)

        if (nodeSnap.exists()) {
          const val = nodeSnap.val()
          const json = JSON.stringify(val)
          const sizeInBytes = new Blob([json]).size

          const numChildren = typeof val === 'object' && val !== null
            ? Object.keys(val).length
            : 0

          this.nodeSizes.push({
            node: key,
            sizeBytes: sizeInBytes,
            numChildren
          })
        } else {
          this.nodeSizes.push({
            node: key,
            sizeBytes: 0,
            numChildren: 0
          })
        }
      } catch (error) {
        console.error(`Error reading node '${key}':`, error)
        this.nodeSizes.push({
          node: key,
          sizeBytes: -1,
          numChildren: -1
        })
      }
    }

    // Sort the table by node name alphabetically
    this.nodeSizes.sort((a, b) => a.node.localeCompare(b.node))
  }


  get totalSizeBytes(): number {
    return this.nodeSizes
      .filter(item => item.sizeBytes >= 0)
      .reduce((sum, item) => sum + item.sizeBytes, 0)
  }
}
